import React from 'react';

import { Typography } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  caption: {
    color: theme.appColors.iconsTypeMuted,
    fontSize: 12,
    lineHeight: '16px',
  },
  card: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    minWidth: 0,
    padding: theme.spacing(2, 2.5),
  },
  count: {
    color: theme.appColors.iconsTypeMuted,
    fontSize: 16,
    fontWeight: 400,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  headerLead: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  // As tall as a header button, so the title stays centered beside one.
  headerWithControls: {
    minHeight: 32,
  },
  heading: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  title: {
    color: theme.appColors.strongType,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '24px',
  },
}));

interface TaskPageCardProps {
  children: React.ReactNode;
  /** Muted line under the heading, e.g. who can see the section. */
  caption?: React.ReactNode;
  className?: string;
  count?: number;
  /** Right side of the heading, e.g. an add button. */
  headerAction?: React.ReactNode;
  /** Just after the title, e.g. a Team / External switch or a Round pill. */
  headerControl?: React.ReactNode;
  icon?: LucideIcon;
  title?: string;
}

/** One white card on the task page. A card without a title lets its content bring its own. */
export function TaskPageCard({
  caption,
  children,
  className,
  count,
  headerAction,
  headerControl,
  icon: Icon,
  title,
}: TaskPageCardProps): JSX.Element {
  const { classes, cx } = useStyles();

  return (
    <section className={cx(classes.card, className)}>
      {Boolean(title) && (
        <div className={classes.heading}>
          <div
            className={cx(
              classes.header,
              Boolean(headerAction || headerControl) && classes.headerWithControls
            )}
          >
            <div className={classes.headerLead}>
              <TaskPageCardTitle icon={Icon}>
                {title}
                {count !== undefined && <span className={classes.count}>{`  (${count})`}</span>}
              </TaskPageCardTitle>
              {headerControl}
            </div>
            {headerAction}
          </div>
          {Boolean(caption) && <span className={classes.caption}>{caption}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

interface TaskPageCardTitleProps {
  children: React.ReactNode;
  icon?: LucideIcon;
}

/** A card's icon and title, for content that brings its own header row. */
export function TaskPageCardTitle({ children, icon: Icon }: TaskPageCardTitleProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <span className={classes.headerLead}>
      {Icon && <Icon size={18} />}
      <Typography className={classes.title} component="h2">
        {children}
      </Typography>
    </span>
  );
}
